import React, { useEffect, useRef } from "react";
import { Box } from "theme-ui";
import { loadGooglePlaces, parsePlaceAddress } from "./googlePlaces";

/**
 * Text input wired to Google Places address autocomplete (US addresses).
 */
export default function StreetAddressAutocomplete({
  label = "Street address",
  value,
  onChange,
  onPlaceSelected,
  id = "street-address",
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);

  onChangeRef.current = onChange;
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    let cancelled = false;
    let loadedGoogle;

    loadGooglePlaces()
      .then((googleMaps) => {
        if (cancelled || !googleMaps || !inputRef.current) return;
        loadedGoogle = googleMaps;

        const autocomplete = new googleMaps.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["address"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "formatted_address"],
          }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const parsed = parsePlaceAddress(place);
          onChangeRef.current?.(parsed.line1);
          onPlaceSelectedRef.current?.(parsed);
        });

        autocompleteRef.current = autocomplete;
      })
      .catch((err) => {
        console.log("Google Places autocomplete unavailable", err);
      });

    return () => {
      cancelled = true;
      if (autocompleteRef.current && loadedGoogle) {
        loadedGoogle.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  return (
    <Box>
      <Box as="label" htmlFor={id} sx={{ display: "block", mb: 1, fontSize: 1 }}>
        {label}
      </Box>
      <input
        id={id}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        autoComplete="off"
        style={{
          width: "100%",
          height: 40,
          padding: "0 8px",
          border: "1px solid #c8c8c8",
          borderRadius: 4,
          fontSize: 14,
          boxSizing: "border-box",
          background: "white",
        }}
      />
    </Box>
  );
}
